import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { debounce } from 'lodash';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import EmailChangeModal from '../../components/settings/EmailChangeModal';
import InfoModal from '../../components/ui/InfoModal';
import InterviewSection from './components/InterviewSection';

// Import modular components
import PersonalInfoTab from './components/PersonalInfoTab';
import ProfessionalTab from './components/ProfessionalTab';
import SkillsTab from './components/SkillsTab';
import PreferencesTab from './components/PreferencesTab';
import WorkExperienceTab from './components/WorkExperienceTab';
import VisibilityTab from './components/VisibilityTab';
import VANotificationsTab from './components/VANotificationsTab';
import VAPrivacyTab from './components/VAPrivacyTab';
import VASecurityTab from './components/VASecurityTab';

// Import utilities
import { validateProfileField, calculateProfileCompletion, getMissingProfileFields } from './utils/profileValidation';
import { serializeProfileData, normalizeArrayField } from './utils/dataProcessing';
import { PROFILE_CONSTANTS } from './constants/profileConstants';
import VAHeaderNav from 'components/navigation/VAHeaderNav';
import { profilePictureService } from '../../utils/profilePictureService';
//import BreadcrumbNavigation from 'components/navigation/BreadcrumbNavigation';

const VAProfileSettings = () => {
  const { userProfile, updateProfile, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tab from URL hash, default to 'personal'
  const getTabFromUrl = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['personal', 'professional', 'experience', 'skills', 'preferences', 'notifications', 'privacy', 'security', 'visibility'];
    return validTabs.includes(hash) ? hash : 'personal';
  };
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [profileInitialized, setProfileInitialized] = useState(false);
  
  // Email change modal state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangeNotification, setEmailChangeNotification] = useState('');
  
  // Admin review modal state
  const [showAdminReviewModal, setShowAdminReviewModal] = useState(false);
  
  // Missing fields modal state
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  
  // Use ref to track the last processed profile ID to prevent infinite loops
  const lastProfileIdRef = useRef(null);
  
  // Initialize profile data with proper defaults
  const [profileData, setProfileData] = useState(() => ({
    // Basic fields
    full_name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    website: '',
    linkedin: '',
    title: '',
    timezone: '',
    education: '',
    portfolio_description: '',
    profile_picture: '',
    
    // Numeric fields
    hourly_rate: '',
    experience_years: '',
    minimum_rate: '',
    maximum_rate: '',
    
    // Array fields - ensure they're always arrays, never objects
    skills: [],
    languages: [],
    certifications: [],
    soft_skills: [],
    work_samples: [],
    preferred_project_types: [],
    other_profiles: [],
    work_experience: [],
    security_questions: [],
    
    // Enum/Select fields
    availability_status: 'available',
    preferred_work_type: 'remote',
    years_of_experience_category: 'entry',
    communication_style: 'professional',
    industry_specialization: '',
    
    // Additional fields
    availability_hours: '',
    portfolio_url: '',
    github_url: '',
    video_introduction_url: '',
    assessment_scores: {},
    
    // Settings fields
    notification_settings: {
      email: {
        jobMatches: true,
        applicationUpdates: true,
        messages: true,
        projectInvitations: true,
        paymentUpdates: true,
        profileViews: false,
        weeklyDigest: true,
        marketingEmails: false,
        securityAlerts: true,
        systemUpdates: false
      },
      push: {
        messages: true,
        applicationUpdates: true,
        jobMatches: false
      },
      frequency: {
        digestFrequency: 'daily'
      }
    },
    privacy_settings: {
      profile_visible: true,
      contact_info_visible: false,
      experience_visible: true,
      skills_visible: true,
      portfolio_visible: true,
      hourly_rate_visible: true,
      availability_visible: true,
      education_visible: true,
      certifications_visible: true,
      languages_visible: true,
      allow_direct_messages: true,
      show_online_status: false,
      allow_profile_indexing: true,
      public_portfolio: false,
      hide_from_search: false,
      anonymous_browsing: false
    },
    security_settings: {
      two_factor_auth: false,
      login_notifications: false,
      session_timeout: 30,
      suspicious_activity_alerts: true,
      device_tracking: true,
      password_expiry_days: 90,
      allow_multiple_sessions: true,
      require_verification_for_profile_changes: false,
      client_data_protection: true,
      work_history_privacy: false
    }
  }));

  // Memoized computed values
  const profileCompletion = useMemo(() => 
    calculateProfileCompletion(profileData), [profileData]
  );
  
  const missingFields = useMemo(() => 
    getMissingProfileFields(profileData), [profileData]
  );

  const tabs = useMemo(() => [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'professional', label: 'Professional', icon: 'Briefcase' },
    { id: 'experience', label: 'Work Experience', icon: 'Briefcase' },
    { id: 'skills', label: 'Certificates & Languages', icon: 'Star' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'visibility', label: 'Visibility', icon: 'Eye' }
  ], []);

  // Handler to change tab and update URL
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  }, [navigate]);

  // Sync activeTab with URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['personal', 'professional', 'experience', 'skills', 'preferences', 'notifications', 'privacy', 'security', 'visibility'];
    if (validTabs.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Track showAdminReviewModal state changes
  useEffect(() => {
    console.log('[VA Profile Settings] showAdminReviewModal state changed:', showAdminReviewModal);
  }, [showAdminReviewModal]);

  // Load user profile data on mount - prevent infinite loops
  useEffect(() => {
    if (userProfile && userProfile.id && lastProfileIdRef.current !== userProfile.id) {
      setProfileData(prevData => {
        const normalizedProfile = { ...prevData };
        
        // Normalize all data from userProfile
        Object.keys(userProfile).forEach(key => {
          if (userProfile[key] !== null && userProfile[key] !== undefined) {
            // Handle array fields - ensure they're always arrays of strings
            if (PROFILE_CONSTANTS.ARRAY_FIELDS.includes(key)) {
              normalizedProfile[key] = normalizeArrayField(userProfile[key]);
            }
            // Special handling for notification_settings to ensure proper structure
            else if (key === 'notification_settings') {
              const userNotificationSettings = userProfile[key];
              
              // Check if it's the old flat structure and migrate to new nested structure
              if (userNotificationSettings && typeof userNotificationSettings === 'object') {
                // If it has the old flat structure (e.g., email_notifications, message_notifications)
                if (userNotificationSettings.email_notifications !== undefined || 
                    userNotificationSettings.message_notifications !== undefined) {
                  // Migrate to new structure
                  normalizedProfile[key] = {
                    email: {
                      jobMatches: userNotificationSettings.email_notifications !== false,
                      applicationUpdates: userNotificationSettings.application_notifications !== false,
                      messages: userNotificationSettings.message_notifications !== false,
                      projectInvitations: true,
                      paymentUpdates: true,
                      profileViews: false,
                      weeklyDigest: userNotificationSettings.weekly_reports !== false,
                      marketingEmails: userNotificationSettings.marketing_emails === true,
                      securityAlerts: true,
                      systemUpdates: false
                    },
                    push: {
                      messages: true,
                      applicationUpdates: true,
                      jobMatches: false
                    },
                    frequency: {
                      digestFrequency: 'daily'
                    }
                  };
                } 
                // If it already has the new nested structure, use it
                else if (userNotificationSettings.email || userNotificationSettings.push) {
                  // Merge with defaults to ensure all fields exist
                  normalizedProfile[key] = {
                    email: {
                      ...prevData.notification_settings.email,
                      ...(userNotificationSettings.email || {})
                    },
                    push: {
                      ...prevData.notification_settings.push,
                      ...(userNotificationSettings.push || {})
                    },
                    frequency: {
                      ...prevData.notification_settings.frequency,
                      ...(userNotificationSettings.frequency || {})
                    }
                  };
                } else {
                  // Unknown structure, use defaults
                  normalizedProfile[key] = prevData.notification_settings;
                }
              } else {
                // No notification settings from user, keep defaults
                normalizedProfile[key] = prevData.notification_settings;
              }
            }
            // Special handling for privacy_settings and security_settings
            else if (key === 'privacy_settings' || key === 'security_settings') {
              if (userProfile[key] && typeof userProfile[key] === 'object') {
                // Merge with defaults to ensure all fields exist
                normalizedProfile[key] = {
                  ...prevData[key],
                  ...userProfile[key]
                };
              } else {
                normalizedProfile[key] = prevData[key];
              }
            }
            else {
              normalizedProfile[key] = userProfile[key];
            }
          }
        });
        
        return normalizedProfile;
      });
      lastProfileIdRef.current = userProfile.id;
      setProfileInitialized(true);
    }
  }, [userProfile]);

  // Debounced auto-save (disabled as requested)
  const debouncedAutoSave = useCallback(
    debounce(async (data) => {
      // Auto-save functionality disabled
      // Not calling updateProfile to prevent auto-saving
    }, 2000),
    [] // Remove dependencies since auto-save is disabled
  );

  // Enhanced input change handler
  const handleInputChange = useCallback((field, value) => {
    // Debug logging for notification_settings
    if (field === 'notification_settings' && process.env.NODE_ENV === 'development') {
      console.log('handleInputChange: Received notification_settings update:', value);
    }
    
    setProfileData(prev => {
      const newData = { ...prev };
      
      // Handle array fields properly
      if (PROFILE_CONSTANTS.ARRAY_FIELDS.includes(field)) {
        // Special handling for work_experience - preserve the complete objects but ensure clean data
        if (field === 'work_experience' && Array.isArray(value)) {
          try {
            // Create a clean copy with only the essential properties
            const cleanExperiences = value.map(exp => {
              if (exp && typeof exp === 'object') {
                return {
                  position: String(exp.position || ''),
                  company: String(exp.company || ''),
                  location: String(exp.location || ''),
                  type: String(exp.type || ''),
                  start_date: String(exp.start_date || ''),
                  end_date: String(exp.end_date || ''),
                  current: Boolean(exp.current),
                  description: String(exp.description || ''),
                  skills: Array.isArray(exp.skills) ? exp.skills.map(String) : [],
                  achievements: Array.isArray(exp.achievements) ? exp.achievements.map(String) : [],
                  responsibilities: Array.isArray(exp.responsibilities) ? exp.responsibilities.map(String) : []
                };
              }
              return null;
            }).filter(Boolean);
            
            newData[field] = cleanExperiences;
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Error processing work experience:', error);
            }
            newData[field] = [];
          }
        } else {
          newData[field] = normalizeArrayField(value);
        }
      } else {
        newData[field] = value;
      }
      
      return newData;
    });
    
    // Validate field and set unsaved changes
    validateProfileField(field, value, setFieldErrors);
    setHasUnsavedChanges(true);
    
    // Auto-save disabled - not calling debouncedAutoSave
  }, []);

  // Array manipulation handlers
  const handleArrayAdd = useCallback((field, item) => {
    // Allow both string and object items (for languages, certifications, etc.)
    if (!item) return;
    
    setProfileData(prev => {
      const currentArray = normalizeArrayField(prev[field]);
      
      // For objects (like languages and certifications with name/url), check by name property
      if (typeof item === 'object' && item !== null && item.name) {
        const exists = currentArray.some(existing => 
          (typeof existing === 'object' && existing !== null && existing.name === item.name) ||
          (typeof existing === 'string' && existing === item.name)
        );
        if (exists) return prev;
        
        return {
          ...prev,
          [field]: [...currentArray, item]
        };
      } else if (typeof item === 'string') {
        // For strings (like individual skills)
        if (currentArray.includes(item)) return prev;
        
        return {
          ...prev,
          [field]: [...currentArray, item]
        };
      }
      
      return prev;
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleArrayRemove = useCallback((field, item) => {
    setProfileData(prev => {
      const currentArray = normalizeArrayField(prev[field]);
      
      // Handle object items (like languages and certifications with name/url)
      if (typeof item === 'object' && item !== null && item.name) {
        return {
          ...prev,
          [field]: currentArray.filter(i => {
            if (typeof i === 'object' && i !== null) {
              return i.name !== item.name;
            } else if (typeof i === 'string') {
              return i !== item.name;
            }
            return true;
          })
        };
      } else {
        // Handle string items (like individual skills)
        return {
          ...prev,
          [field]: currentArray.filter(i => i !== item)
        };
      }
    });
    setHasUnsavedChanges(true);
  }, []);

  // Form validation
  // Standalone validation function (not used in callbacks to avoid circular deps)
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate required fields
    PROFILE_CONSTANTS.REQUIRED_FIELDS.forEach(field => {
      const value = profileData[field];
      if (!value || 
          (Array.isArray(value) && value.length === 0) || 
          (typeof value === 'string' && !value.trim())) {
        errors[field] = 'This field is required';
        isValid = false;
      }
    });

    setFieldErrors(errors);
    return isValid;
  };

  // Enhanced save handler with better error handling
  const handleSaveProfile = useCallback(async () => {
    // Debug logging for notification_settings
    if (process.env.NODE_ENV === 'development') {
      console.log('handleSaveProfile: Current notification_settings:', profileData.notification_settings);
    }
    
    // Clear previous errors
    setError('');
    setFieldErrors({});
    setIsSuccess(false);
    
    // Validate that at least email and full_name are present (minimal required fields)
    const errors = {};
    let hasValidationErrors = false;

    // Only require email and full_name as true required fields
    const minimalRequiredFields = ['email', 'full_name'];
    minimalRequiredFields.forEach(field => {
      const value = profileData[field];
      if (!value || 
          (Array.isArray(value) && value.length === 0) || 
          (typeof value === 'string' && !value.trim())) {
        errors[field] = `${field.replace('_', ' ')} is required`;
        hasValidationErrors = true;
      }
    });

    // If minimal validation fails, show errors and stop
    if (hasValidationErrors) {
      setFieldErrors(errors);
      setError('Please enter your email and full name before saving.');
      return;
    }

    setIsLoading(true);

    try {
      const serializedData = serializeProfileData(profileData);
      
      // Ensure we have a user ID
      if (!user?.id) {
        throw new Error('User not authenticated. Please log in again.');
      }

      const result = await updateProfile(serializedData);

      if (result?.success) {
        setIsSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setIsSuccess(false), 3000);
      } else {
        const errorMessage = result?.error || 'Failed to update profile. Please check your connection and try again.';
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err?.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [profileData, updateProfile, user]);

  // Email change handler
  const handleEmailChangeInitiated = useCallback((message) => {
    setEmailChangeNotification(message);
    setShowEmailChangeModal(false);
    // Clear notification after 10 seconds
    setTimeout(() => setEmailChangeNotification(''), 10000);
  }, []);

  // Profile picture upload handler
  const handleProfilePictureUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('[VA Profile Settings] Starting profile picture upload for user:', user.id);
      
      // Upload using the profile picture service
      const result = await profilePictureService.uploadProfilePicture(user.id, file);

      console.log('[VA Profile Settings] IMMEDIATELY after upload - result:', result);
      console.log('[VA Profile Settings] Upload result:', result);

      if (!result.success) {
        console.error('[VA Profile Settings] Upload failed:', result.error);
        console.error('[VA Profile Settings] Result object:', result);
        setError(result.error || 'Failed to upload profile picture');
        return;
      }

      console.log('[VA Profile Settings] Upload was successful, checking conditions...');
      console.log('[VA Profile Settings] result.immediate:', result.immediate);
      console.log('[VA Profile Settings] result.needsReview:', result.needsReview);

      if (result.immediate) {
        // First upload - applied immediately
        setIsSuccess(true);
        setError('');
        
        console.log('[VA Profile Settings] First upload successful, updating profile data');
        
        // Create updated profile data with the new image
        const updatedProfileData = {
          ...profileData,
          profile_picture: result.url,
          profile_picture_change_count: 1,
          profile_picture_status: 'approved'
        };
        
        // Update local profile data
        setProfileData(updatedProfileData);

        // Immediately save to database
        console.log('[VA Profile Settings] Saving updated profile picture to database');
        if (updateProfile) {
          await updateProfile(updatedProfileData);
        }

        // Show success message
        setTimeout(() => {
          setIsSuccess(false);
        }, 3000);
      } else if (result.needsReview) {
        // Subsequent upload - pending review
        console.log('[VA Profile Settings] Upload needs review, updating pending status');
        console.log('[VA Profile Settings] Current showAdminReviewModal:', showAdminReviewModal);
        
        // Create updated profile data with pending picture
        const updatedProfileData = {
          ...profileData,
          profile_picture_pending: result.url,
          profile_picture_status: 'pending'
        };
        
        console.log('[VA Profile Settings] Updated profile data:', updatedProfileData);
        // Update local state to reflect pending status
        setProfileData(updatedProfileData);

        // Show info message FIRST
        setError(''); // Clear any previous errors
        console.log('[VA Profile Settings] Setting showAdminReviewModal to true');
        console.log('[VA Profile Settings] About to call setShowAdminReviewModal(true)');
        setShowAdminReviewModal(true);
        console.log('[VA Profile Settings] Called setShowAdminReviewModal(true)');

        // Don't call updateProfile() here - it will cause a profile refresh that resets state
        // The database was already updated by the service, just keep local state in sync
        console.log('[VA Profile Settings] Skipping updateProfile to preserve modal state');
      }
    } catch (err) {
      console.error('[VA Profile Settings] Catch block - Error details:', {
        message: err?.message,
        error: err,
        stack: err?.stack
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Error uploading profile picture:', err);
      }
      setError(err?.message || 'Failed to upload profile picture. Please try again.');
    } finally {
      console.log('[VA Profile Settings] Finally block - setting isLoading to false');
      setIsLoading(false);
      // Reset the file input
      event.target.value = '';
    }
  }, [user, profileData, updateProfile, showAdminReviewModal]);

  // Common props for tab components
  const commonTabProps = {
    profileData,
    fieldErrors,
    onInputChange: handleInputChange,
    onArrayAdd: handleArrayAdd,
    onArrayRemove: handleArrayRemove,
    onProfilePictureUpload: handleProfilePictureUpload,
    onEmailChangeClick: () => setShowEmailChangeModal(true),
    emailChangeNotification,
    isUploadingPicture: isLoading
  };

  return (
    <div className="min-h-screen bg-background">
      <VAHeaderNav />
      <Helmet>
        <title>Profile Settings - VA Sprouts</title>
        <meta name="description" content="Manage your VA profile settings and information" />
      </Helmet>

      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          <div className="py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Profile Settings
                </h1>
                <p className="text-text-secondary">
                  Manage your profile information and preferences
                </p>
                {/* Profile completion indicator */}
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <span>Profile completion:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-32">
                      <div 
                        className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-300"
                        style={{ width: `${profileCompletion}%` }}
                      />
                    </div>
                    <span className="font-medium text-primary">{profileCompletion}%</span>
                  </div>
                  
                  {/* Missing fields notification */}
                  {profileCompletion < 100 && missingFields.totalMissing > 0 && (
                    <div className="mt-2 text-xs text-text-secondary">
                      <button
                        onClick={() => setShowMissingFieldsModal(true)}
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Icon name="Info" size={12} />
                        {missingFields.totalMissing} field{missingFields.totalMissing !== 1 ? 's' : ''} incomplete
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {hasUnsavedChanges && (
                  <span className="text-sm text-warning-600 flex items-center gap-1">
                    <Icon name="Clock" size={16} />
                    Unsaved changes
                  </span>
                )}
                <Button
                  variant="primary"
                  iconName="Save"
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="hidden md:flex"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>

            {/* AI Interview Section */}
            <div id="interview-section" className="mb-8">
              <InterviewSection />
            </div>

            {/* Success/Error Messages */}
            {isSuccess && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-success-700">
                  <Icon name="CheckCircle" size={20} />
                  <span className="font-medium">Profile updated successfully!</span>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-error-700">
                  <Icon name="AlertCircle" size={20} />
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Tab Navigation and Content - Responsive Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Vertical Tab Navigation */}
              <div className="w-full lg:w-64 xl:w-72 shrink-0">
                {/* Mobile/Tablet Tab Selection (Dropdown for < lg screens) */}
                <div className="lg:hidden mb-4">
                  <select 
                    value={activeTab} 
                    onChange={(e) => handleTabChange(e.target.value)}
                    className="w-full p-3 border border-border rounded-md bg-surface"
                  >
                    {tabs.map((tab) => (
                      <option key={tab.id} value={tab.id}>
                        {tab.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Desktop Vertical Tabs (lg and above) */}
                <div className="hidden lg:flex lg:flex-col bg-surface rounded-lg border border-border overflow-hidden">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center space-x-3 py-4 px-4 transition-colors border-l-4 text-sm ${
                        activeTab === tab.id
                          ? 'border-l-primary bg-primary-50 text-primary font-medium'
                          : 'border-l-transparent text-text-secondary hover:bg-gray-50 hover:text-text-primary'
                      }`}
                    >
                      <Icon name={tab.icon} size={18} />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 bg-surface rounded-lg border border-border p-4 md:p-6">
                {activeTab === 'personal' && <PersonalInfoTab {...commonTabProps} />}
                {activeTab === 'professional' && <ProfessionalTab {...commonTabProps} />}
                {activeTab === 'experience' && <WorkExperienceTab {...commonTabProps} />}
                {activeTab === 'skills' && <SkillsTab {...commonTabProps} />}
                {activeTab === 'preferences' && <PreferencesTab {...commonTabProps} />}
                {activeTab === 'notifications' && (
                  <VANotificationsTab
                    profileData={profileData}
                    onInputChange={handleInputChange}
                  />
                )}
                {activeTab === 'privacy' && (
                  <VAPrivacyTab
                    profileData={profileData}
                    onInputChange={handleInputChange}
                  />
                )}
                {activeTab === 'security' && (
                  <VASecurityTab
                    profileData={profileData}
                    onInputChange={handleInputChange}
                  />
                )}
                {activeTab === 'visibility' && <VisibilityTab />}
              </div>
            </div>

            {/* Mobile Save Button */}
            <div className="md:hidden mt-6">
              <Button
                variant="primary"
                iconName="Save"
                onClick={handleSaveProfile}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
            
          </div>
        </div>
      </main>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={showEmailChangeModal}
        onClose={() => setShowEmailChangeModal(false)}
        currentEmail={profileData.email}
        userId={user?.id}
        userRole="va"
        onEmailChangeInitiated={handleEmailChangeInitiated}
      />
      
      {/* Admin Review Modal */}
      {showAdminReviewModal && (
        <>
          <InfoModal
            title="Picture Submitted for Review"
            message="Profile picture submitted for admin review. You'll be notified once it's approved."
            buttonLabel="OK"
            type="info"
            onClose={() => {
              console.log('[VA Profile Settings] Closing admin review modal');
              setShowAdminReviewModal(false);
            }}
          />
          <div style={{position: 'fixed', bottom: '20px', right: '20px', padding: '10px', background: 'green', color: 'white', zIndex: 40}}>
            DEBUG: Modal showing = {String(showAdminReviewModal)}
          </div>
        </>
      )}
      
      {/* Missing Fields Modal */}
      {showMissingFieldsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
                  <Icon name="Info" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-text-primary">
                    Incomplete Profile Fields
                  </h3>
                  <p className="text-sm text-text-secondary mt-1">
                    {missingFields.totalMissing} field{missingFields.totalMissing !== 1 ? 's' : ''} remaining to reach 100%
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMissingFieldsModal(false)}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto p-6 flex-1">
              {/* Required Fields */}
              {missingFields.hasMissingRequired && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="AlertCircle" size={18} className="text-error-600" />
                    <h4 className="font-semibold text-text-primary">
                      Required Fields ({missingFields.missingRequired.length})
                    </h4>
                  </div>
                  <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                    <p className="text-sm text-error-700 mb-3">
                      These fields are required to have a complete profile:
                    </p>
                    <ul className="space-y-2">
                      {missingFields.missingRequired.map((field, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-text-primary">
                          <div className="w-1.5 h-1.5 rounded-full bg-error-600" />
                          {field.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Optional Fields */}
              {missingFields.hasMissingOptional && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="CheckCircle" size={18} className="text-primary" />
                    <h4 className="font-semibold text-text-primary">
                      Optional Fields ({missingFields.missingOptional.length})
                    </h4>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <p className="text-sm text-primary-700 mb-3">
                      Adding these fields will improve your profile:
                    </p>
                    <ul className="space-y-2 max-h-60 overflow-y-auto">
                      {missingFields.missingOptional.map((field, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-text-primary">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          {field.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Completion Progress */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-primary">Overall Completion</span>
                  <span className="text-sm font-semibold text-primary">{profileCompletion}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-300"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowMissingFieldsModal(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowMissingFieldsModal(false);
                  // Navigate to the first tab with missing fields
                  if (missingFields.hasMissingRequired || missingFields.hasMissingOptional) {
                    // Simple logic: go to personal tab as it has most basic fields
                    handleTabChange('personal');
                  }
                }}
              >
                Complete Profile
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VAProfileSettings;

